import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  TextField,
  DialogContentText,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";

import dayjs from "dayjs";
import "dayjs/locale/es";
import isoWeek from "dayjs/plugin/isoWeek";

import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";

import * as XLSX from "xlsx";
import moment from "moment";

import { Alert, Button, Divider, Snackbar } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

dayjs.locale("es");
dayjs.extend(isoWeek);

// -------------------------------
// Helpers
// -------------------------------
const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const statusMeta = (status) => {
  const s = String(status || "SCHEDULED").toUpperCase();

  // Azul = pendiente
  if (s === "SCHEDULED" || s === "STARTED") {
    return {
      label: s,
      bg: "rgba(25,118,210,0.15)",
      fg: "#1565C0",
      bd: "rgba(21,101,192,0.35)",
    };
  }

  // Verde = cumplido
  if (s === "FINISHED" || s === "DONE" || s === "COMPLETED") {
    return {
      label: s,
      bg: "rgba(46,125,50,0.15)",
      fg: "#2E7D32",
      bd: "rgba(46,125,50,0.35)",
    };
  }

  // Rojo = no cumplido
  if (s === "CANCELLED" || s === "NO_SHOW" || s === "MISSED") {
    return {
      label: s,
      bg: "rgba(198,40,40,0.15)",
      fg: "#C62828",
      bd: "rgba(198,40,40,0.35)",
    };
  }

  // Default
  return {
    label: s,
    bg: "rgba(0,0,0,0.06)",
    fg: "#111",
    bd: "rgba(0,0,0,0.15)",
  };
};

const normalizeDateYYYYMMDD = (v) => {
  if (!v) return "";

  // Date
  if (v instanceof Date && !isNaN(v.getTime())) {
    return dayjs(v).format("YYYY-MM-DD");
  }

  // Excel serial number
  if (typeof v === "number") {
    const parsed = XLSX.SSF.parse_date_code(v);
    if (parsed) {
      const d = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));
      return dayjs(d).format("YYYY-MM-DD");
    }
  }

  // string
  const s = String(v).trim();
  // si viene "2025-12-19"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // intenta dayjs
  const d = dayjs(s);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const normalizeTimeHHmm = (v) => {
  if (!v) return "";

  // Date (a veces Excel entrega Date)
  if (v instanceof Date && !isNaN(v.getTime())) {
    return dayjs(v).format("HH:mm");
  }

  // number serial (fracción del día)
  if (typeof v === "number") {
    // Excel time serial => day fraction
    const totalMinutes = Math.round(v * 24 * 60);
    const hh = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const mm = String(totalMinutes % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  }

  const s = String(v).trim();
  // si viene "9:00" -> "09:00"
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${String(m[1]).padStart(2, "0")}:${m[2]}`;
  return "";
};
// -------------------------------
// Component
// -------------------------------
const Turnos = () => {
  const [month, setMonth] = useState(dayjs()); // mes actual visible
  const [loading, setLoading] = useState(false);

  // data
  const [shifts, setShifts] = useState([]); // turnos del mes (raw)
  const [driverMap, setDriverMap] = useState(new Map()); // driverId -> {name}
  const [storeMap, setStoreMap] = useState(new Map()); // storeId  -> {name}

  // filters
  const [filterStoreId, setFilterStoreId] = useState("");
  const [filterDriverId, setFilterDriverId] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "", SCHEDULED, FINISHED, ...

  const monthLabel = useMemo(() => month.format("MMMM YYYY"), [month]);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftModalMode, setShiftModalMode] = useState("create"); // "create" | "edit"
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [savingShift, setSavingShift] = useState(false);

  const [shiftForm, setShiftForm] = useState({
    storeId: "",
    driverId: "",
    date: dayjs().format("YYYY-MM-DD"),
    startTime: "09:00",
    endTime: "18:00",
    status: "SCHEDULED",
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [newShift, setNewShift] = useState({
    storeId: "",
    driverId: "",
    date: dayjs().format("YYYY-MM-DD"),
    startTime: "09:00",
    endTime: "18:00",
    status: "SCHEDULED",
  });

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [uploading, setUploading] = useState(false);

  const loadMonth = useCallback(async () => {
    setLoading(true);

    try {
      const start = month.startOf("month").format("YYYY-MM-DD");
      const end = month.endOf("month").format("YYYY-MM-DD");

      // 1) Shifts por rango de fecha (string ISO permite range)
      const qShifts = query(
        collection(db, "xpress_ebiexShifts"),
        where("date", ">=", start),
        where("date", "<=", end)
      );

      const snap = await getDocs(qShifts);
      const monthShifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setShifts(monthShifts);

      // 2) resolver nombres de drivers/stores (solo los usados)
      const driverIds = Array.from(
        new Set(monthShifts.map((s) => s.driverId).filter(Boolean))
      );
      const storeIds = Array.from(
        new Set(monthShifts.map((s) => s.storeId).filter(Boolean))
      );

      // Drivers (documentId in chunks de 10)
      const dMap = new Map();
      for (const part of chunk(driverIds, 10)) {
        const qDrivers = query(
          collection(db, "drivers"),
          where(documentId(), "in", part)
        );
        const dSnap = await getDocs(qDrivers);
        dSnap.docs.forEach((x) => {
          const data = x.data() || {};
          const name =
            data.name ||
            data.fullName ||
            data.displayName ||
            data.email ||
            x.id;
          dMap.set(x.id, { id: x.id, name, ...data });
        });
      }

      // Stores
      const sMap = new Map();
      for (const part of chunk(storeIds, 10)) {
        const qStores = query(
          collection(db, "xpress_ebiexStores"),
          where(documentId(), "in", part)
        );
        const sSnap = await getDocs(qStores);
        sSnap.docs.forEach((x) => {
          const data = x.data() || {};
          const name = data.name || x.id;
          sMap.set(x.id, { id: x.id, name, ...data });
        });
      }

      setDriverMap(dMap);
      setStoreMap(sMap);
    } catch (e) {
      console.error("loadMonth error:", e);
      setShifts([]);
      setDriverMap(new Map());
      setStoreMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [month]);

  const navigate = useNavigate();

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const handleUploadXlsx = useCallback(
    async (file) => {
      if (!file) return;

      setUploading(true);
      try {
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });

        const firstSheetName = wb.SheetNames?.[0];
        if (!firstSheetName) throw new Error("El archivo no tiene hojas.");

        const ws = wb.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }); // array de objetos

        if (!rows.length) {
          setToast({
            open: true,
            msg: "El Excel no tiene filas.",
            severity: "warning",
          });
          return;
        }

        // Normalizar filas
        const normalized = rows
          .map((r, idx) => {
            const id = String(r.id || r.ID || "").trim();
            const driverId = String(r.driverId || r.driverID || "").trim();
            const storeId = String(r.storeId || r.storeID || "").trim();

            const date = normalizeDateYYYYMMDD(r.date || r.fecha);
            const startTime = normalizeTimeHHmm(
              r.startTime || r.horaInicio || r.start
            );
            const endTime = normalizeTimeHHmm(r.endTime || r.horaFin || r.end);

            const status = String(r.status || "SCHEDULED")
              .trim()
              .toUpperCase();

            return {
              __row: idx + 2, // aprox (header en 1)
              id,
              driverId,
              storeId,
              date,
              startTime,
              endTime,
              status,
            };
          })
          .filter((x) => x.driverId || x.storeId || x.date); // limpia vacías

        // Validación mínima
        const invalid = normalized.filter(
          (x) =>
            !x.driverId || !x.storeId || !x.date || !x.startTime || !x.endTime
        );
        if (invalid.length) {
          const first = invalid[0];
          setToast({
            open: true,
            severity: "error",
            msg: `Hay filas inválidas. Ej: fila ${first.__row} (driverId/storeId/date/start/end obligatorios)`,
          });
          return;
        }

        // 1) ids presentes
        const idsProvided = normalized.map((x) => x.id).filter(Boolean);

        // 2) detectar cuáles ya existen (para "si posee id no editarlo")
        const existingIds = new Set();
        for (const part of chunk(idsProvided, 10)) {
          const qRef = query(
            collection(db, "xpress_ebiexShifts"),
            where(documentId(), "in", part)
          );
          const s = await getDocs(qRef);
          s.docs.forEach((d) => existingIds.add(d.id));
        }

        // 3) escribir por batches (máx 500 ops)
        let created = 0;
        let skipped = 0;

        const ops = [];

        for (const row of normalized) {
          // Si viene ID:
          if (row.id) {
            if (existingIds.has(row.id)) {
              skipped++;
              continue; // NO editar
            }
            // crear con ese id (no existe)
            ops.push({
              kind: "fixed",
              id: row.id,
              data: row,
            });
            continue;
          }

          // sin id => crear nuevo
          ops.push({ kind: "auto", data: row });
        }

        // Ejecutar batches
        const parts = chunk(ops, 450); // deja margen
        for (const p of parts) {
          const batch = writeBatch(db);

          for (const op of p) {
            if (op.kind === "fixed") {
              const ref = doc(collection(db, "xpress_ebiexShifts"), op.id);

              batch.set(ref, {
                id: op.id,
                driverId: op.data.driverId,
                storeId: op.data.storeId,
                date: op.data.date,
                startTime: op.data.startTime,
                endTime: op.data.endTime,
                status: op.data.status || "SCHEDULED",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              created++;
            } else {
              const ref = doc(collection(db, "xpress_ebiexShifts")); // auto-id
              const newId = ref.id;

              batch.set(ref, {
                id: newId,
                driverId: op.data.driverId,
                storeId: op.data.storeId,
                date: op.data.date,
                startTime: op.data.startTime,
                endTime: op.data.endTime,
                status: op.data.status || "SCHEDULED",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              created++;
            }
          }

          await batch.commit();
        }

        setToast({
          open: true,
          severity: "success",
          msg: `Importación lista. Creados: ${created}. Saltados (id existentes): ${skipped}.`,
        });

        await loadMonth(); // refresca calendario
      } catch (e) {
        console.error(e);
        setToast({
          open: true,
          severity: "error",
          msg: "Error importando XLSX.",
        });
      } finally {
        setUploading(false);
      }
    },
    [loadMonth]
  );

  const openCreate = (dateStr) => {
    setNewShift((p) => ({
      ...p,
      date: dateStr || dayjs().format("YYYY-MM-DD"),
    }));
    setCreateOpen(true);
  };

  const closeCreate = () => setCreateOpen(false);

  const driverOptions = useMemo(() => {
    return Array.from(driverMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [driverMap]);

  const deleteShift = async () => {
    if (!editingShiftId) return;

    setSavingShift(true);
    try {
      await deleteDoc(doc(db, "xpress_ebiexShifts", editingShiftId));

      setToast({
        open: true,
        severity: "success",
        msg: "Turno eliminado.",
      });

      setConfirmDeleteOpen(false);
      setShiftModalOpen(false);
      await loadMonth();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        severity: "error",
        msg: "No se pudo eliminar el turno.",
      });
    } finally {
      setSavingShift(false);
    }
  };

  const validateShiftForm = () => {
    if (!shiftForm.storeId || !shiftForm.driverId || !shiftForm.date)
      return false;
    if (!shiftForm.startTime || !shiftForm.endTime) return false;
    return true;
  };

  const saveShift = async () => {
    if (!validateShiftForm()) {
      setToast({
        open: true,
        severity: "warning",
        msg: "Falta tienda, conductor, fecha u horarios.",
      });
      return;
    }

    setSavingShift(true);
    try {
      if (shiftModalMode === "create") {
        const ref = doc(collection(db, "xpress_ebiexShifts")); // auto-id
        const id = ref.id;

        await setDoc(ref, {
          id,
          storeId: shiftForm.storeId,
          driverId: shiftForm.driverId,
          date: shiftForm.date,
          startTime: shiftForm.startTime,
          endTime: shiftForm.endTime,
          status: String(shiftForm.status || "SCHEDULED").toUpperCase(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        setToast({ open: true, severity: "success", msg: "Turno creado." });
      } else {
        if (!editingShiftId) throw new Error("No editingShiftId");

        const ref = doc(db, "xpress_ebiexShifts", editingShiftId);

        await updateDoc(ref, {
          storeId: shiftForm.storeId,
          driverId: shiftForm.driverId,
          date: shiftForm.date,
          startTime: shiftForm.startTime,
          endTime: shiftForm.endTime,
          status: String(shiftForm.status || "SCHEDULED").toUpperCase(),
          updatedAt: serverTimestamp(),
        });

        setToast({
          open: true,
          severity: "success",
          msg: "Turno actualizado.",
        });
      }

      setShiftModalOpen(false);
      await loadMonth();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        severity: "error",
        msg: "No se pudo guardar el turno.",
      });
    } finally {
      setSavingShift(false);
    }
  };

  const openCreateShift = () => {
    setShiftModalMode("create");
    setEditingShiftId(null);
    setShiftForm({
      storeId: "",
      driverId: "",
      date: dayjs().format("YYYY-MM-DD"),
      startTime: "09:00",
      endTime: "18:00",
      status: "SCHEDULED",
    });
    setShiftModalOpen(true);
  };

  const openEditShift = (shift) => {
    setShiftModalMode("edit");
    setEditingShiftId(shift.id);
    setShiftForm({
      storeId: shift.storeId || "",
      driverId: shift.driverId || "",
      date: shift.date || dayjs().format("YYYY-MM-DD"),
      startTime: shift.startTime || "09:00",
      endTime: shift.endTime || "18:00",
      status: String(shift.status || "SCHEDULED").toUpperCase(),
    });
    setShiftModalOpen(true);
  };

  const closeShiftModal = () => setShiftModalOpen(false);

  const modalDriverOptions = useMemo(() => {
    if (!newShift.storeId) return [];
    const store = storeMap.get(newShift.storeId);
    const allowed = Array.isArray(store?.driverIds) ? store.driverIds : null;

    if (!allowed) return driverOptions; // fallback: muestra todos

    return allowed.map((id) => ({
      id,
      name: driverMap.get(id)?.name || id,
    }));
  }, [newShift.storeId, storeMap, driverOptions, driverMap]);

  const shiftFormDriverOptions = useMemo(() => {
    if (!shiftForm.storeId) return [];

    const store = storeMap.get(shiftForm.storeId);
    const allowed = Array.isArray(store?.driverIds) ? store.driverIds : null;

    // Si la tienda no trae driverIds => fallback: muestra todos los drivers conocidos
    if (!allowed) return driverOptions;

    return allowed.map((id) => ({
      id,
      name: driverMap.get(id)?.name || id,
    }));
  }, [shiftForm.storeId, storeMap, driverOptions, driverMap]);

  const handleStoreChangeShiftForm = (storeId) => {
    setShiftForm((p) => ({ ...p, storeId, driverId: "" }));
  };

  // cuando elijo tienda => reset conductor
  const handleStoreChangeNewShift = (storeId) => {
    setNewShift((p) => ({ ...p, storeId, driverId: "" }));
  };

  // crear en Firestore
  const createShift = async () => {
    if (!newShift.storeId || !newShift.driverId || !newShift.date) {
      setToast({
        open: true,
        severity: "warning",
        msg: "Falta seleccionar tienda, conductor y fecha.",
      });
      return;
    }
    if (!newShift.startTime || !newShift.endTime) {
      setToast({
        open: true,
        severity: "warning",
        msg: "Faltan horas de inicio/fin.",
      });
      return;
    }

    setCreating(true);
    try {
      const ref = doc(collection(db, "xpress_ebiexShifts")); // auto id
      const id = ref.id;

      await setDoc(ref, {
        id,
        storeId: newShift.storeId,
        driverId: newShift.driverId,
        date: newShift.date,
        startTime: newShift.startTime,
        endTime: newShift.endTime,
        status: String(newShift.status || "SCHEDULED").toUpperCase(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setToast({
        open: true,
        severity: "success",
        msg: "Turno creado.",
      });

      setCreateOpen(false);
      await loadMonth();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        severity: "error",
        msg: "No se pudo crear el turno.",
      });
    } finally {
      setCreating(false);
    }
  };

  const exportarTurnosXLSX = async ({ turns = [], filters = {} }) => {
    // Encabezados
    const matrizReporte = [
      [
        "id",
        "date",
        "driverId",
        "driverName",
        "storeId",
        "storeName",
        "startTime",
        "endTime",
        "status",
      ],
      ...turns.map((t) => [
        t.id || "",
        t.date || "",
        t.driverId || "",
        t.driverName || "",
        t.storeId || "",
        t.storeName || "",
        t.startTime || "",
        t.endTime || "",
        t.status || "SCHEDULED",
      ]),
    ];

    const libro = XLSX.utils.book_new();
    const hoja = XLSX.utils.aoa_to_sheet(matrizReporte);
    XLSX.utils.book_append_sheet(libro, hoja, "Turnos");

    const extra = [
      filters?.month ? `_${filters.month}` : "",
      filters?.storeId ? `_store-${filters.storeId}` : "",
      filters?.driverId ? `_driver-${filters.driverId}` : "",
    ].join("");

    XLSX.writeFile(
      libro,
      `Turnos${extra}_${moment(Date.now()).format("YYYY-MM-DD_HH_mm_ss")}.xlsx`
    );
  };

  // options para filtros (solo lo que existe en el mes)
  const storeOptions = useMemo(() => {
    return Array.from(storeMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [storeMap]);

  // aplicar filtros en memoria
  const filteredShifts = useMemo(() => {
    return (shifts || []).filter((s) => {
      if (filterStoreId && s.storeId !== filterStoreId) return false;
      if (filterDriverId && s.driverId !== filterDriverId) return false;
      if (filterStatus) {
        const st = String(s.status || "SCHEDULED").toUpperCase();
        if (st !== filterStatus) return false;
      }
      return true;
    });
  }, [shifts, filterStoreId, filterDriverId, filterStatus]);

  // shifts agrupados por date
  const shiftsByDate = useMemo(() => {
    const map = new Map(); // dateStr -> shifts[]
    for (const s of filteredShifts) {
      const key = String(s.date || "");
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }

    // orden por hora dentro del día
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a.startTime || "").localeCompare(String(b.startTime || ""))
      );
      map.set(k, arr);
    }

    return map;
  }, [filteredShifts]);

  // construir grilla 6 semanas (LUN a DOM)
  const gridDays = useMemo(() => {
    const start = month.startOf("month").startOf("isoWeek"); // lunes
    const days = [];
    for (let i = 0; i < 42; i++) days.push(start.add(i, "day"));
    return days;
  }, [month]);

  const weekDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const goPrev = () => setMonth((m) => m.subtract(1, "month"));
  const goNext = () => setMonth((m) => m.add(1, "month"));
  const goToday = () => setMonth(dayjs());

  const cardSx = {
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  };

  return (
    <Box
      sx={{ p: { xs: 1, md: 2 }, width: "100%", backgroundColor: "#F8F8F8" }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          width: "100%",
          backgroundColor: "#F8F8F8",
          p: "10px",
          justifyContent: "space-between",
        }}
      >
        <Grid2 xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box sx={{ flexGrow: 1, p: "10px", color: "#111" }}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                Turnos
              </Typography>

              <ButtonLike
                sx={{ color: "#111" }}
                onClick={() => navigate(`/dashboard/turnos/asistencia`)}
                icon={<DoneAllIcon sx={{ color: "#09d31aff" }} />}
                text="ver asistenacias"
              />
              <Typography variant="body2" sx={{ opacity: 0.7, color: "#111" }}>
                Vista mensual • Filtra por tienda / conductor • Colores por
                estado
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Mes anterior">
                <IconButton onClick={goPrev}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{
                  fontWeight: 900,
                  minWidth: 160,
                  textAlign: "center",
                  color: "#111",
                }}
              >
                {monthLabel}
              </Typography>

              <Tooltip title="Mes siguiente">
                <IconButton onClick={goNext}>
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>

              <ButtonLike
                sx={{ color: "#111" }}
                onClick={goToday}
                icon={<TodayIcon sx={{ color: "#111" }} />}
                text="Hoy"
              />
            </Stack>
          </Stack>
        </Grid2>

        <Grid2 xs={12}>
          <Card sx={{ ...cardSx, p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 900 }}>
                  Importar / Exportar turnos (Excel)
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Subir: crea nuevos turnos. Si la fila trae <b>id</b>, no se
                  edita: si existe se salta, si no existe se crea. Descargar:
                  exporta todos los turnos según los filtros actuales.
                </Typography>
              </Box>

              <Divider
                flexItem
                orientation="vertical"
                sx={{ display: { xs: "none", md: "block" } }}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploading}
                >
                  {uploading ? "Subiendo…" : "Subir XLSX"}
                  <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) handleUploadXlsx(file);
                    }}
                  />
                </Button>

                <Button
                  variant="contained"
                  onClick={() =>
                    exportarTurnosXLSX({
                      turns: filteredShifts,
                      filters: {
                        month: month.format("YYYY-MM"), // ✅ string estable
                        storeId: filterStoreId, // ✅ tu state real
                        driverId: filterDriverId, // ✅ tu state real
                        status: filterStatus, // (opcional) si quieres en el nombre
                      },
                    })
                  }
                >
                  Descargar
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Grid2>
        {/* Filtros */}
        <Grid2 xs={12}>
          <Card sx={{ ...cardSx, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tienda</InputLabel>
                  <Select
                    label="Tienda"
                    value={filterStoreId}
                    onChange={(e) => setFilterStoreId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas</em>
                    </MenuItem>
                    {storeOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Conductor</InputLabel>
                  <Select
                    label="Conductor"
                    value={filterDriverId}
                    onChange={(e) => setFilterDriverId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {driverOptions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    <MenuItem value="SCHEDULED">Pendiente (SCHEDULED)</MenuItem>
                    <MenuItem value="STARTED">Pendiente (STARTED)</MenuItem>
                    <MenuItem value="FINISHED">Cumplido (FINISHED)</MenuItem>
                    <MenuItem value="CANCELLED">
                      No cumplido (CANCELLED)
                    </MenuItem>
                    <MenuItem value="NO_SHOW">No cumplido (NO_SHOW)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              <Chip
                label="Pendiente"
                sx={{ bgcolor: "rgba(21,101,192,0.15)", color: "#1565C0" }}
              />
              <Chip
                label="Cumplido"
                sx={{ bgcolor: "rgba(46,125,50,0.15)", color: "#2E7D32" }}
              />
              <Chip
                label="No cumplido"
                sx={{ bgcolor: "rgba(198,40,40,0.15)", color: "#C62828" }}
              />
              <Box sx={{ flex: 1 }} />
              <Chip
                variant="outlined"
                label={
                  loading
                    ? "Cargando…"
                    : `${filteredShifts.length} turno(s) en el mes (con filtros)`
                }
              />
            </Stack>
          </Card>
        </Grid2>

        {/* Calendario */}
        <Grid2 sx={{ width: "100%" }} xs={12}>
          <Card sx={{ ...cardSx, overflow: "hidden" }}>
            {/* Header días semana */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "rgba(0,0,0,0.02)",
              }}
            >
              {weekDays.map((w) => (
                <Box
                  key={w}
                  sx={{
                    p: 1,
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: 12,
                    opacity: 0.75,
                    borderRight: "1px solid rgba(0,0,0,0.04)",
                    "&:last-of-type": { borderRight: "none" },
                  }}
                >
                  {w}
                </Box>
              ))}
            </Box>

            {/* Grid días */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gridAutoRows: { xs: "120px", md: "140px" },
              }}
            >
              {gridDays.map((d) => {
                const dateStr = d.format("YYYY-MM-DD");
                const isCurrentMonth = d.month() === month.month();
                const isToday = dateStr === dayjs().format("YYYY-MM-DD");
                const dayEvents = shiftsByDate.get(dateStr) || [];

                return (
                  <Box
                    key={dateStr}
                    sx={{
                      p: 1,
                      borderRight: "1px solid rgba(0,0,0,0.04)",
                      borderBottom: "1px solid rgba(0,0,0,0.04)",
                      bgcolor: isCurrentMonth ? "#fff" : "rgba(0,0,0,0.015)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Día */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 900,
                          fontSize: 13,
                          bgcolor: isToday
                            ? "rgba(21,101,192,0.15)"
                            : "transparent",
                          color: isToday
                            ? "#1565C0"
                            : isCurrentMonth
                            ? "#111"
                            : "rgba(0,0,0,0.45)",
                        }}
                      >
                        {d.date()}
                      </Box>

                      {dayEvents.length ? (
                        <Chip
                          size="small"
                          label={dayEvents.length}
                          sx={{ height: 20, fontSize: 11, opacity: 0.8 }}
                        />
                      ) : null}
                    </Stack>

                    {/* Eventos */}
                    <Box
                      sx={{
                        mt: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {dayEvents.slice(0, 4).map((s) => {
                        const meta = statusMeta(s.status);
                        const driverName =
                          driverMap.get(s.driverId)?.name ||
                          s.driverId ||
                          "Conductor";
                        const storeName =
                          storeMap.get(s.storeId)?.name ||
                          s.storeId ||
                          "Tienda";
                        const time =
                          s.startTime && s.endTime
                            ? `${s.startTime}-${s.endTime}`
                            : "";

                        return (
                          <Tooltip
                            key={s.id}
                            title={`${time} • ${driverName} • ${storeName} • ${meta.label}`}
                            arrow
                          >
                            <Box
                              onClick={() => openEditShift(s)}
                              sx={{
                                px: 0.75,
                                py: 0.4,
                                borderRadius: 1.5,
                                border: `1px solid ${meta.bd}`,
                                bgcolor: meta.bg,
                                color: meta.fg,
                                fontSize: 11,
                                fontWeight: 800,
                                lineHeight: 1.15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "default",
                              }}
                            >
                              {time ? `${time} • ` : ""}
                              {driverName} — {storeName}
                            </Box>
                          </Tooltip>
                        );
                      })}

                      {dayEvents.length > 4 ? (
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.7, mt: 0.25 }}
                        >
                          +{dayEvents.length - 4} más…
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {loading ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={22} />
              </Box>
            ) : null}
          </Card>
        </Grid2>
      </Grid2>
      <Dialog
        open={shiftModalOpen}
        onClose={closeShiftModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {shiftModalMode === "create" ? "Crear turno" : "Editar turno"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {shiftModalMode === "edit" && editingShiftId ? (
              <TextField
                fullWidth
                label="ID (no editable)"
                value={editingShiftId}
                disabled
              />
            ) : null}

            <FormControl fullWidth>
              <InputLabel>Tienda</InputLabel>
              <Select
                label="Tienda"
                value={shiftForm.storeId}
                onChange={(e) => handleStoreChangeShiftForm(e.target.value)}
              >
                {storeOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!shiftForm.storeId}>
              <InputLabel>Conductor</InputLabel>
              <Select
                label="Conductor"
                value={shiftForm.driverId}
                onChange={(e) =>
                  setShiftForm((p) => ({ ...p, driverId: e.target.value }))
                }
              >
                {shiftFormDriverOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Fecha"
              type="date"
              value={shiftForm.date}
              onChange={(e) =>
                setShiftForm((p) => ({ ...p, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora inicio"
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((p) => ({ ...p, startTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora fin"
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((p) => ({ ...p, endTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={shiftForm.status}
                onChange={(e) =>
                  setShiftForm((p) => ({ ...p, status: e.target.value }))
                }
              >
                <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
                <MenuItem value="STARTED">STARTED</MenuItem>
                <MenuItem value="FINISHED">FINISHED</MenuItem>
                <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                <MenuItem value="NO_SHOW">NO_SHOW</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {shiftModalMode === "edit" ? (
            <Button
              color="error"
              variant="outlined"
              disabled={savingShift}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Eliminar
            </Button>
          ) : (
            <span />
          )}
          <Button
            onClick={closeShiftModal}
            variant="outlined"
            disabled={savingShift}
          >
            Cancelar
          </Button>
          <Button
            onClick={saveShift}
            variant="contained"
            disabled={savingShift}
          >
            {savingShift ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Fab
        color="primary"
        onClick={openCreateShift}
        sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 2000 }}
      >
        <AddIcon />
      </Fab>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Crear turno</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Tienda</InputLabel>
              <Select
                label="Tienda"
                value={newShift.storeId}
                onChange={(e) => handleStoreChangeNewShift(e.target.value)}
              >
                {storeOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!newShift.storeId}>
              <InputLabel>Conductor</InputLabel>
              <Select
                label="Conductor"
                value={newShift.driverId}
                onChange={(e) =>
                  setNewShift((p) => ({ ...p, driverId: e.target.value }))
                }
              >
                {shiftFormDriverOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Fecha"
              type="date"
              value={newShift.date}
              onChange={(e) =>
                setNewShift((p) => ({ ...p, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora inicio"
                  type="time"
                  value={newShift.startTime}
                  onChange={(e) =>
                    setNewShift((p) => ({ ...p, startTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora fin"
                  type="time"
                  value={newShift.endTime}
                  onChange={(e) =>
                    setNewShift((p) => ({ ...p, endTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={newShift.status}
                onChange={(e) =>
                  setNewShift((p) => ({ ...p, status: e.target.value }))
                }
              >
                <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
                <MenuItem value="STARTED">STARTED</MenuItem>
                <MenuItem value="FINISHED">FINISHED</MenuItem>
                <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                <MenuItem value="NO_SHOW">NO_SHOW</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeCreate} variant="outlined" disabled={creating}>
            Cancelar
          </Button>
          <Button onClick={createShift} variant="contained" disabled={creating}>
            {creating ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Botón flotante */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Eliminar turno</DialogTitle>

        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres eliminar este turno? Esta acción no se puede
            deshacer.
          </DialogContentText>

          {editingShiftId ? (
            <Box sx={{ mt: 2 }}>
              <Chip label={`ID: ${editingShiftId}`} />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            variant="outlined"
            disabled={savingShift}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={deleteShift}
            disabled={savingShift}
          >
            {savingShift ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Botón mini simple para no meter más imports
const ButtonLike = ({ onClick, icon, text }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 0.75,
      borderRadius: 2,
      border: "1px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      userSelect: "none",
      "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
    }}
  >
    {icon}
    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{text}</Typography>
  </Box>
);

export default Turnos;
